import { supabase } from '../lib/supabase';

export const purchasesService = {
  // Mark a listing as sold (create a purchase record)
  async markAsSold(listingId, buyerId) {
    try {
      console.log('Marking listing as sold:', { listingId, buyerId });
      
      // First get the listing to get the seller_id
      const { data: listing, error: listingError } = await supabase
        .from('listings')
        .select('user_id, status')
        .eq('id', listingId)
        .single();

      if (listingError) {
        console.error('Error fetching listing:', listingError);
        throw listingError;
      }

      // Check if purchase record already exists
      const { data: existingPurchase } = await supabase
        .from('purchases')
        .select('id')
        .eq('listing_id', listingId)
        .eq('buyer_id', buyerId)
        .single();

      if (existingPurchase) {
        // If purchase exists, just update the listing status if needed
        if (listing.status !== 'sold') {
          const { error: updateError } = await supabase
            .from('listings')
            .update({ status: 'sold' })
            .eq('id', listingId);

          if (updateError) {
            console.error('Error updating listing status:', updateError);
            throw updateError;
          }
        }
        
        console.log('Purchase record already exists, listing status updated');
        return existingPurchase;
      }

      // Check if already sold but no purchase record
      if (listing.status === 'sold') {
        throw new Error('This item has already been marked as sold');
      }

      // Create purchase record
      const { data, error } = await supabase
        .from('purchases')
        .insert([{
          listing_id: listingId,
          buyer_id: buyerId,
          seller_id: listing.user_id
        }])
        .select()
        .single();

      if (error) {
        console.error('Error creating purchase:', error);
        throw error;
      }

      // Update listing status to 'sold'
      const { error: updateError } = await supabase
        .from('listings')
        .update({ status: 'sold' })
        .eq('id', listingId);

      if (updateError) {
        console.error('Error updating listing status:', updateError);
        throw updateError;
      }

      console.log('Successfully marked listing as sold:', data);
      return data;
    } catch (error) {
      console.error('Error marking listing as sold:', error);
      throw error;
    }
  },

  // Check if user bought a specific listing
  async hasUserBoughtListing(listingId, userId) {
    try {
      console.log('Checking if user bought listing:', { listingId, userId });
      
      // First check the purchases table
      const { data: purchaseData, error: purchaseError } = await supabase
        .from('purchases')
        .select('id')
        .eq('listing_id', listingId)
        .eq('buyer_id', userId)
        .single();

      if (!purchaseError && purchaseData) {
        return true;
      }

      // Also check the transactions table for approved payments
      const { data: transactionData, error: transactionError } = await supabase
        .from('transactions')
        .select('id')
        .eq('listing_id', listingId)
        .eq('buyer_id', userId)
        .eq('approval_status', 'approved')
        .single();

      if (!transactionError && transactionData) {
        return true;
      }

      // No purchase or approved transaction found
      return false;
    } catch (error) {
      console.error('Error checking if user bought listing:', error);
      throw error;
    }
  },

  // Get user's purchase history
  async getUserPurchases(userId) {
    try {
      console.log('Fetching user purchases:', userId);
      
      // Get purchases
      const { data: purchases, error: purchasesError } = await supabase
        .from('purchases')
        .select('*')
        .eq('buyer_id', userId)
        .order('purchase_date', { ascending: false });

      if (purchasesError) {
        console.error('Error fetching purchases:', purchasesError);
        throw purchasesError;
      }

      if (!purchases || purchases.length === 0) {
        return [];
      }

      // Get listing details
      const listingIds = purchases.map(p => p.listing_id);
      const { data: listings, error: listingsError } = await supabase
        .from('listings')
        .select('*')
        .in('id', listingIds);

      if (listingsError) {
        console.error('Error fetching listings:', listingsError);
      }

      // Get seller profiles
      const sellerIds = [...new Set(purchases.map(p => p.seller_id))];
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, name, avatar_url')
        .in('id', sellerIds);

      if (profilesError) {
        console.error('Error fetching seller profiles:', profilesError);
      }

      // Combine the data
      const purchasesWithDetails = purchases.map(purchase => {
        const listing = listings?.find(l => l.id === purchase.listing_id);
        const seller = profiles?.find(p => p.id === purchase.seller_id);
        
        return {
          ...purchase,
          listing: listing || { id: purchase.listing_id, title: 'Unknown Item' },
          seller: seller || { id: purchase.seller_id, name: 'Unknown Seller', avatar_url: null }
        };
      });

      return purchasesWithDetails;
    } catch (error) {
      console.error('Error fetching user purchases:', error);
      throw error;
    }
  },

  // Get seller's sales history
  async getUserSales(userId) {
    try {
      console.log('Fetching user sales:', userId);
      
      // Get sales
      const { data: sales, error: salesError } = await supabase
        .from('purchases')
        .select('*')
        .eq('seller_id', userId)
        .order('purchase_date', { ascending: false });

      if (salesError) {
        console.error('Error fetching sales:', salesError);
        throw salesError;
      }

      if (!sales || sales.length === 0) {
        return [];
      }

      // Get listing details
      const listingIds = sales.map(s => s.listing_id);
      const { data: listings, error: listingsError } = await supabase
        .from('listings')
        .select('*')
        .in('id', listingIds);

      if (listingsError) {
        console.error('Error fetching listings:', listingsError);
      }

      // Get buyer profiles
      const buyerIds = [...new Set(sales.map(s => s.buyer_id))];
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, name, avatar_url')
        .in('id', buyerIds);

      if (profilesError) {
        console.error('Error fetching buyer profiles:', profilesError);
      }

      // Combine the data
      const salesWithDetails = sales.map(sale => {
        const listing = listings?.find(l => l.id === sale.listing_id);
        const buyer = profiles?.find(p => p.id === sale.buyer_id);
        
        return {
          ...sale,
          listing: listing || { id: sale.listing_id, title: 'Unknown Item' },
          buyer: buyer || { id: sale.buyer_id, name: 'Unknown Buyer', avatar_url: null }
        };
      });

      return salesWithDetails;
    } catch (error) {
      console.error('Error fetching user sales:', error);
      throw error;
    }
  }
};
